// apis
import { getAuthAPI } from 'shared/api/authResolverLoader';

// helpers
import { isAuthenticatedSessionMarked, isGuestSessionMarked } from 'shared/api/grahakSession';
import { isAuthRecoveryInProgress } from 'shared/helpers/api/authRecoveryState';
import { getCachedCustomerData } from 'shared/helpers/userCache';

// redux
import { ActionTypes } from './actions';

/**
 * Transform the Grahak user response to the legacy app-user shape consumed by
 * the header, account controls, and boot flow.
 */
const transformUserResponse = (newResponse: any) => {
  const { response, data: rawData = {} } = newResponse || {};
  const data = rawData || {};

  if (response) {
    const {
      form_Key = '',
      cartCount,
      cart_count,
      customerData = {},
      base_location = 'IN',
    } = response;

    return {
      formKey: form_Key,
      cartCount: cartCount ?? cart_count ?? 0,
      customerData,
      base_location,
    };
  }

  const { userDetails = {}, isGuestUser, cartCount, form_Key = '', formKey = '' } = data;
  const normalizedUserDetails = userDetails || {};

  if (isGuestUser || Object.keys(normalizedUserDetails).length === 0) {
    return {
      formKey: form_Key || formKey,
      cartCount: cartCount || 0,
      customerData: {},
      base_location: 'IN',
    };
  }

  const {
    userId = '',
    email = '',
    mobile = '',
    firstName = '',
    lastName = '',
    fullName = '',
    hideEmail,
    isEmailVerified = false,
    dob = '',
    gender,
    imageLink = '',
  } = normalizedUserDetails;

  const customerData = {
    id: userId,
    email,
    mobile,
    firstname: firstName || fullName,
    lastname: lastName,
    hideEmail: hideEmail !== undefined ? hideEmail : true,
    isEmailVerified,
    fullName,
    dob,
    gender: gender !== undefined ? String(gender) : '',
    imageLink,
    allowMergeAccount: false,
    newUser: 0,
  };

  return {
    formKey: form_Key || formKey,
    cartCount: cartCount || 0,
    customerData,
    base_location: 'IN',
  };
};

const getAuthenticatedCachedCustomerData = () => {
  if (isGuestSessionMarked() || !isAuthenticatedSessionMarked() || isAuthRecoveryInProgress()) {
    return undefined;
  }

  const cachedCustomerData = getCachedCustomerData();
  if (
    !cachedCustomerData ||
    typeof cachedCustomerData !== 'object' ||
    Object.keys(cachedCustomerData).length === 0
  ) {
    return undefined;
  }

  return cachedCustomerData;
};

export const fetchUser = (): any => {
  return (dispatch: any) => {
    // The desktop cart runs in an iframe. Its auth recovery updates a separate
    // Redux store, but the Grahak session marker is shared with the parent.
    if (isGuestSessionMarked()) {
      dispatch({ type: ActionTypes.SET_GUEST_SESSION });
    }

    dispatch({ type: ActionTypes.FETCH_USER_PROGRESS });
    return getAuthAPI()
      .then((AuthAPI) => AuthAPI.getLoggedInUser())
      .then((data) => {
        const transformedData = transformUserResponse(data);
        const {
          formKey,
          cartCount,
          customerData: responseCustomerData,
          base_location,
        } = transformedData;
        // A request started with old credentials can resolve after recovery.
        const customerData = isGuestSessionMarked() ? {} : responseCustomerData;

        dispatch({
          type: ActionTypes.FETCH_USER_SUCCESS,
          payload: { formKey, cartCount, customerData, base_location },
        });

        return { formKey, cartCount, customerData, base_location };
      })
      .catch(() => {
        const isGuestSession = isGuestSessionMarked();
        if (isGuestSession) {
          dispatch({ type: ActionTypes.SET_GUEST_SESSION });
        }

        const cachedCustomerData = getAuthenticatedCachedCustomerData();
        dispatch(
          cachedCustomerData
            ? {
                type: ActionTypes.FETCH_USER_FAILED,
                payload: { customerData: cachedCustomerData },
              }
            : { type: ActionTypes.FETCH_USER_FAILED }
        );

        return {
          customerData: isGuestSession ? {} : cachedCustomerData,
          isUserFetchError: true,
          isAuthenticatedSession:
            !isGuestSession && !isAuthRecoveryInProgress() && isAuthenticatedSessionMarked(),
        };
      });
  };
};
